import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  IconButton,
  Stack,
  Switch,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { appConfig } from '../../config/app';
import { useApp } from '../../context/AppContext';

const CartRecommendations = () => {
  const { actions } = useApp();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      const res = await axios.get(`${appConfig.api.baseURL}/cart-recommendations`);
      if (res.data?.success) {
        setItems(res.data.data.items || []);
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to load cart recommendations', 'error');
      }
    } catch (error) {
      console.error('Error loading cart recommendations:', error);
      actions.showSnackbar('Failed to load cart recommendations', 'error');
    }
  }, [actions]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${appConfig.api.baseURL}/products`, {
          params: { q: term, status: 'active', limit: 20 }
        });
        if (!cancelled) {
          setResults(Array.isArray(res.data?.data) ? res.data.data : []);
        }
      } catch (error) {
        console.error('Error searching products:', error);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const handleAdd = async (product) => {
    setAddingId(product.id);
    try {
      const res = await axios.post(`${appConfig.api.baseURL}/cart-recommendations`, {
        productId: product.id,
        isActive: true
      });
      if (res.data?.success) {
        actions.showSnackbar('Product added to cart recommendations', 'success');
        setQuery('');
        setResults([]);
        loadItems();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to add product', 'error');
      }
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Failed to add product', 'error');
    } finally {
      setAddingId(null);
    }
  };

  const handleToggle = async (item, isActive) => {
    try {
      const res = await axios.put(`${appConfig.api.baseURL}/cart-recommendations/${item.id}`, { isActive });
      if (res.data?.success) {
        setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, isActive } : row)));
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const handleSortBlur = async (item, value) => {
    const sortOrder = Number(value);
    if (!Number.isFinite(sortOrder) || sortOrder === item.sortOrder) return;
    try {
      const res = await axios.put(`${appConfig.api.baseURL}/cart-recommendations/${item.id}`, { sortOrder });
      if (res.data?.success) {
        loadItems();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to update order', 'error');
      }
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Failed to update order', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${appConfig.api.baseURL}/cart-recommendations/${id}`);
      if (res.data?.success) {
        actions.showSnackbar('Cart recommendation removed', 'success');
        loadItems();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to delete recommendation', 'error');
      }
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Failed to delete recommendation', 'error');
    }
  };

  const configuredIds = new Set(items.map((item) => item.productId));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Cart recommendations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active products are returned on the cart page. Inactive products stay saved and are not recommended.
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardHeader title="Add a product" subheader="Search active products by name." />
        <CardContent>
          <TextField
            label="Search products"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
            placeholder="Type at least 2 characters"
          />
          {searching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              No active products matched.
            </Typography>
          )}
          <List>
            {results.map((product) => {
              const alreadyAdded = configuredIds.has(product.id);
              return (
                <ListItemButton
                  key={product.id}
                  disabled={alreadyAdded || addingId === product.id}
                  onClick={() => handleAdd(product)}
                >
                  <ListItemText
                    primary={product.name}
                    secondary={alreadyAdded ? 'Already added' : product.category || product.id}
                  />
                  <AddIcon fontSize="small" />
                </ListItemButton>
              );
            })}
          </List>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Configured products"
          subheader="Only active rows are sent to the customer cart page, in this order."
        />
        <CardContent>
          {items.length === 0 && (
            <Typography color="text.secondary">No cart recommendations yet.</Typography>
          )}
          <Stack spacing={2}>
            {items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                    <Chip
                      size="small"
                      label={item.isActive ? 'Active' : 'Inactive'}
                      color={item.isActive ? 'success' : 'default'}
                    />
                    <Typography variant="body1">{item.productName || item.productId}</Typography>
                  </Stack>
                </Box>
                <TextField
                  label="Order"
                  type="number"
                  size="small"
                  defaultValue={item.sortOrder}
                  key={`${item.id}-${item.sortOrder}`}
                  onBlur={(e) => handleSortBlur(item, e.target.value)}
                  inputProps={{ step: 1 }}
                  sx={{ width: 100 }}
                />
                <Stack direction="row" alignItems="center">
                  <Typography variant="body2">Active</Typography>
                  <Switch
                    checked={item.isActive === true}
                    onChange={(e) => handleToggle(item, e.target.checked)}
                    inputProps={{ 'aria-label': `Set ${item.productName || 'product'} active` }}
                  />
                </Stack>
                <IconButton aria-label="Remove recommendation" onClick={() => handleDelete(item.id)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CartRecommendations;
